import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Heading,
  VStack,
  Alert,
  AlertIcon,
  ButtonGroup,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
} from '@chakra-ui/react';
import { serviceApi } from '../services/api';

function ServiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    duration_minutes: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchService();
    }
    // eslint-disable-next-line
  }, [id]);

  const fetchService = async () => {
    try {
      const response = await serviceApi.getById(id);
      setFormData(response.data);
    } catch (err) {
      setError('Failed to fetch service');
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await serviceApi.update(id, formData);
        toast({
          title: 'Service updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await serviceApi.create(formData);
        toast({
          title: 'Service created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      navigate('/app/services');
    } catch (err) {
      setError(isEdit ? 'Failed to update service' : 'Failed to create service');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>
        {isEdit ? 'Edit Service' : 'Add New Service'}
      </Heading>

      {error && (
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box as="form" onSubmit={handleSubmit} bg="white" p={8} borderRadius="xl" boxShadow="sm" maxW="2xl">
        <VStack spacing={5} align="stretch">
          <FormControl isRequired>
            <FormLabel>Service Name</FormLabel>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Botox, Dermal Filler, Laser Treatment"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Brief description of the service"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Category</FormLabel>
            <Input
              name="category"
              value={formData.category}
              onChange={handleChange}
              placeholder="e.g., Injectables, Lasers, Skincare, Consultation"
              list="category-suggestions"
            />
            <datalist id="category-suggestions">
              <option value="Injectables" />
              <option value="Lasers" />
              <option value="Skincare" />
              <option value="Consultation" />
              <option value="Body Treatments" />
            </datalist>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Price ($)</FormLabel>
            <NumberInput
              min={0}
              precision={2}
              value={formData.price}
              onChange={(valueString) => setFormData((prev) => ({ ...prev, price: valueString }))}
            >
              <NumberInputField placeholder="0.00" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel>Duration (minutes)</FormLabel>
            <NumberInput
              min={0}
              value={formData.duration_minutes}
              onChange={(valueString) =>
                setFormData((prev) => ({ ...prev, duration_minutes: valueString }))
              }
            >
              <NumberInputField placeholder="e.g., 30" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <Checkbox
              name="is_active"
              isChecked={formData.is_active}
              onChange={handleChange}
              colorScheme="purple"
            >
              Active
            </Checkbox>
          </FormControl>

          <ButtonGroup spacing={4} pt={4}>
            <Button type="submit" colorScheme="purple" isLoading={loading} loadingText="Saving">
              {isEdit ? 'Update Service' : 'Create Service'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/app/services')}>
              Cancel
            </Button>
          </ButtonGroup>
        </VStack>
      </Box>
    </Box>
  );
}

export default ServiceForm;
